import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { XCircle, RefreshCw, Users, Building2, GitBranch, ListX } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

const USER_TYPES = [
  { v: "all", label: "Tous" },
  { v: "b2c", label: "B2C" },
  { v: "b2b", label: "B2B" },
];
const FLOWS = [
  { v: "all", label: "Tous flux" },
  { v: "normal", label: "Flux normal" },
  { v: "cross", label: "Flux croisé" },
];
const PERIODS = [
  { v: 30, label: "30 j" },
  { v: 90, label: "90 j" },
  { v: 180, label: "6 mois" },
  { v: 365, label: "12 mois" },
];

function Seg({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 gap-1" data-testid={testid}>
      {options.map((o) => (
        <button key={String(o.v)} type="button" onClick={() => onChange(o.v)}
          data-testid={`${testid}-${o.v}`}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
            value === o.v ? "bg-findo-indigo text-white" : "text-slate-600 hover:bg-slate-50"
          }`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="h-8 w-8 grid place-items-center rounded-lg bg-findo-indigo/10 text-findo-indigo"><Icon size={16} /></div>
      <div>
        <p className="text-[11px] text-slate-500 font-medium leading-none">{label}</p>
        <p className="font-display text-lg font-extrabold text-findo-navy leading-tight">{value}</p>
      </div>
    </div>
  );
}

export default function CancelReasonsPanel() {
  const [userType, setUserType] = useState("all");
  const [flow, setFlow] = useState("all");
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api(`/admin/analytics/cancel-reasons?user_type=${userType}&flow=${flow}&days=${days}`)
      .then((r) => setData(r))
      .catch(() => toast.error("Chargement des motifs impossible"))
      .finally(() => setLoading(false));
  }, [userType, flow, days]);

  useEffect(() => { load(); }, [load]);

  const total = data?.total ?? 0;
  const reasons = data?.reasons || [];
  const maxCount = reasons.reduce((m, r) => Math.max(m, r.count), 0) || 1;

  return (
    <div className="space-y-5" data-testid="cancel-reasons-panel">
      <div className="flex flex-col gap-1">
        <h3 className="font-display text-xl font-extrabold text-findo-navy flex items-center gap-2">
          <ListX size={20} className="text-findo-indigo" /> Motifs d'annulation
        </h3>
        <p className="text-sm text-slate-500">Récapitulatif des motifs d'annulation les plus fréquents, filtrable par type d'utilisateur, flux et période.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Seg options={USER_TYPES} value={userType} onChange={setUserType} testid="cancel-filter-usertype" />
        <Seg options={FLOWS} value={flow} onChange={setFlow} testid="cancel-filter-flow" />
        <Seg options={PERIODS} value={days} onChange={setDays} testid="cancel-filter-period" />
        <button type="button" onClick={load} data-testid="cancel-reasons-refresh"
          className="ml-auto inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
          {loading ? <FindoSpinner size={14} /> : <RefreshCw size={14} />} Actualiser
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat icon={XCircle} label="Annulations" value={total} />
        <MiniStat icon={Users} label="B2C" value={data?.by_user_type?.b2c ?? 0} />
        <MiniStat icon={Building2} label="B2B" value={data?.by_user_type?.b2b ?? 0} />
        <MiniStat icon={GitBranch} label="Normal / Croisé" value={`${data?.by_flow?.normal ?? 0} / ${data?.by_flow?.cross ?? 0}`} />
      </div>

      <div className="rounded-2xl border border-border bg-white card-shadow p-5" data-testid="cancel-reasons-list">
        {loading && !data ? (
          <div className="py-10 grid place-items-center text-slate-400"><FindoSpinner size={22} /></div>
        ) : total === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400" data-testid="cancel-reasons-empty">Aucune annulation sur cette période avec ces filtres.</p>
        ) : (
          <ul className="space-y-3.5">
            {reasons.map((r) => (
              <li key={r.code} data-testid={`cancel-reason-row-${r.code}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-700">{r.label}</span>
                  <span className="text-xs font-bold text-slate-500">{r.count} · {r.pct}%</span>
                </div>
                <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-findo-indigo transition-all"
                    style={{ width: `${Math.max(4, (r.count / maxCount) * 100)}%` }} />
                </div>
                {r.samples?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`cancel-reason-samples-${r.code}`}>
                    {r.samples.map((s, i) => (
                      <span key={i} className="text-[11px] rounded-full bg-slate-50 border border-slate-200 px-2 py-0.5 text-slate-500">« {s} »</span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
